import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  MapPin,
  Clock,
  DollarSign,
  Users,
  ArrowRight,
  Briefcase,
  Heart,
  Globe,
  Coffee,
  Award,
  BarChart3,
  TrendingUp
} from 'lucide-react'
import SEO from '@components/SEO'
import { useContent } from '@context/ContentContext'
import { useFirestore } from '@hooks/useFirestore'
import Reveal from '@components/dimensional/Reveal'
import TiltCard from '@components/dimensional/TiltCard'
import BrandPrimitives from '@components/dimensional/BrandPrimitives'
import RewardsArchitecture from '@components/dimensional/RewardsArchitecture'

const CareersPage = () => {
  const { content, loading: contentLoading } = useContent()
  const careersFirestore = useFirestore('careers')

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: 'easeOut' }
  }

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  // Get page content
  const pageContent = content.pages?.careers || {}
  const heroTitle = pageContent.hero?.title || 'Join Our Team'
  const heroSubtitle = pageContent.hero?.subtitle || 'Build your career with Total Rewards Pro and help shape the future of compensation consulting.'

  // Filter only active careers from Firebase
  const openPositions = careersFirestore.data.filter(career => career.status === 'active')

  // Positioning rail — consistent with how the firm describes itself elsewhere.
  const marks = [
    { value: 'Senior hands', label: 'The person who scopes the file works the file' },
    { value: 'Three regions', label: 'Mandates across APAC, EMEA and the Americas' },
    { value: 'No bench', label: 'Small team on purpose. Nothing gets handed down' }
  ]

  const benefits = [
    {
      icon: Heart,
      title: 'Cover that holds up',
      description: 'Medical, dental and vision cover, plus the wellness programme that sits alongside it.'
    },
    {
      icon: TrendingUp,
      title: 'A ladder you can read',
      description: 'Advancement paths are written down rather than implied, with mentorship and development support behind them.'
    },
    {
      icon: Globe,
      title: 'Markets, not one city',
      description: 'Client work spans APAC, EMEA and the Americas. You will benchmark across pay markets that behave nothing alike.'
    },
    {
      icon: Coffee,
      title: 'Hours that survive a cycle',
      description: 'Flexible schedules, remote options and leave you are expected to take. Merit season is heavy; the time back afterwards is real.'
    },
    {
      icon: BarChart3,
      title: 'The tooling to do it properly',
      description: 'The compensation analytics stack the work actually needs, rather than a spreadsheet and a hunch.'
    },
    {
      icon: Award,
      title: 'Paid for the outcome',
      description: 'Performance-based bonuses and equity participation as the firm grows.'
    }
  ]

  const values = [
    {
      title: 'Show the maths',
      description: 'Every recommendation ships with the model behind it. If the number cannot be defended in a board room, it does not go in the deck.'
    },
    {
      title: 'Say the difficult thing',
      description: 'Clients pay for judgement, not agreement. When a plan will not survive a flat year, we say so in the first meeting.'
    },
    {
      title: 'Own the file',
      description: 'You scope it, you build it, you present it. Nobody here hands work down a pyramid and hopes.'
    },
    {
      title: 'Finish the cycle',
      description: 'Design is the easy half. You stay through the briefings and the first cycle that runs on the plan you designed.'
    }
  ]

  // Stats removed - focus on value proposition for new company

  if (contentLoading || careersFirestore.loading) {
    return (
      <div className="pt-14 min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-white/10 border-t-primary-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-400">Loading careers...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="pt-14 bg-black">
      <SEO
        title="Careers - Join Total Rewards Pro HR Services Team"
        description="Join our team of comprehensive HR services experts and build your career with Total Rewards Pro. Explore open positions in HR consulting, performance management, compliance, and executive compensation across our 9 service domains."
        keywords="HR careers, comprehensive HR services jobs, HR consulting careers, performance appraisal jobs, executive compensation careers, HR compliance jobs, total rewards careers, MECE framework consulting"
      />

      {/* Hero Section */}
      <section className="pt-14 lg:pt-16 pb-20 lg:pb-28 text-white relative overflow-hidden grain">
        {/* Background Image with WebP fallback */}
        <picture className="absolute inset-0">
          <source srcSet="/careers-hero.webp" type="image/webp" />
          <img
            src="/careers-hero.jpg"
            alt="Professional corporate office environment"
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
            decoding="async"
          />
        </picture>

        {/* Depth stack: base darkening, drifting gold mesh, then a vignette that
            pushes the frame edges back and holds the eye on the headline. */}
        <div
          className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/70 to-black/95 pointer-events-none"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 mesh-drift opacity-70 pointer-events-none"
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden="true"
          style={{
            background:
              'radial-gradient(118% 80% at 50% 8%, transparent 28%, rgba(0,0,0,0.55) 62%, rgba(0,0,0,0.92) 100%)'
          }}
        />

        <div className="w-full px-6 sm:px-8 lg:px-12 relative">
          <motion.div
            className="text-center max-w-page mx-auto"
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            <motion.div
              className="inline-flex items-center gap-3 surface-raised-gold edge-lit rounded-pill px-5 py-2.5 mb-10"
              variants={fadeInUp}
            >
              <span
                className="w-1.5 h-1.5 rounded-full bg-primary-500 shadow-[0_0_10px_rgba(255,209,24,0.9)]"
                aria-hidden="true"
              ></span>
              <span className="text-mono-label text-primary-300">
                Careers at TRP
              </span>
            </motion.div>

            <motion.h1
              className="text-display-xl text-white mb-8 text-balance"
              variants={fadeInUp}
            >
              {heroTitle}
            </motion.h1>

            <motion.p
              className="text-body-large text-neutral-300 mb-10 max-w-3xl mx-auto text-balance"
              variants={fadeInUp}
            >
              {heroSubtitle}
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              variants={fadeInUp}
            >
              <button
                onClick={() => {
                  const element = document.getElementById('positions')
                  if (element) {
                    const navbarHeight = 80
                    const elementPosition = element.offsetTop - navbarHeight
                    window.scrollTo({
                      top: elementPosition,
                      behavior: 'smooth'
                    })
                  }
                }}
                className="btn-accent sheen px-8 py-3.5"
              >
                View Open Positions
                <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
              </button>
              <button
                onClick={() => {
                  const element = document.getElementById('culture')
                  if (element) {
                    const navbarHeight = 80
                    const elementPosition = element.offsetTop - navbarHeight
                    window.scrollTo({
                      top: elementPosition,
                      behavior: 'smooth'
                    })
                  }
                }}
                className="inline-flex items-center justify-center rounded-md px-8 py-3.5 text-sm font-semibold text-white surface-raised sheen border border-white/10 transition-colors duration-300 hover:border-primary-500/60 hover:text-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-black font-open-sans"
              >
                How we work
              </button>
            </motion.div>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 hairline" aria-hidden="true"></div>
      </section>

      {/* Positioning rail — pressed into the page rather than floating on it */}
      <section className="bg-black relative overflow-hidden py-12 lg:py-20" aria-label="What working here looks like">
        <div className="max-w-page mx-auto px-6 sm:px-8 lg:px-12 relative">
          <div className="surface-inset rounded-card-lg border border-white/5 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/5">
            {marks.map((mark) => (
              <div key={mark.value} className="px-8 py-8 text-center sm:text-left">
                <div className="text-2xl lg:text-3xl font-bold text-primary-400 font-open-sans tracking-tight mb-2">
                  {mark.value}
                </div>
                <div className="text-body-small text-neutral-400">{mark.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section id="positions" className="section-padding bg-black scroll-mt-20 relative overflow-hidden grain ambient-gold">
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>

        <div className="max-w-page mx-auto px-6 sm:px-8 lg:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.7fr)] gap-10 lg:gap-16 items-center mb-16 lg:mb-20">
            <Reveal className="max-w-3xl">
              <p className="text-mono-label text-primary-500 mb-5 flex items-center gap-3">
                <span>01 / 03</span>
                <span className="w-8 h-px bg-primary-500/40" aria-hidden="true"></span>
                <span>Hiring now</span>
              </p>
              <h2 className="text-section-title text-white mb-6 text-balance">Open positions</h2>
              <p className="text-body-large text-neutral-300">
                Small team, senior work. Whatever is open right now is listed below, with the detail
                you need before you apply.
              </p>
            </Reveal>

            <Reveal delay={120} className="w-full max-w-[380px] mx-auto lg:mx-0 lg:ml-auto">
              <BrandPrimitives
                variant="cluster"
                className="w-full aspect-[360/340]"
                label="Three brand primitives arranged as a cluster, standing for the mix of skills on a small senior team."
              />
              <p className="text-body-small text-neutral-400 mt-6 text-center lg:text-left">
                Roles open when a mandate does. There is no bench and no queue between you and the
                client.
              </p>
            </Reveal>
          </div>

          <div className="space-y-8">
            {openPositions.length > 0 ? openPositions.map((position, index) => (
              <Reveal key={position.id} delay={index * 90}>
                <article className="surface-raised edge-lit rounded-card-lg group">
                  <div className="p-8 lg:p-10">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-8">
                      <div>
                        <h3 className="text-heading-1 text-white mb-5 text-balance">{position.title}</h3>
                        <div className="flex flex-wrap items-center gap-2.5">
                          <div className="flex items-center gap-2 surface-inset rounded-pill px-3.5 py-2">
                            <Briefcase className="w-3.5 h-3.5 text-primary-500/80" aria-hidden="true" />
                            <span className="text-mono-label text-neutral-300">{position.department}</span>
                          </div>
                          <div className="flex items-center gap-2 surface-inset rounded-pill px-3.5 py-2">
                            <MapPin className="w-3.5 h-3.5 text-primary-500/80" aria-hidden="true" />
                            <span className="text-mono-label text-neutral-300">{position.location}</span>
                          </div>
                          <div className="flex items-center gap-2 surface-inset rounded-pill px-3.5 py-2">
                            <Clock className="w-3.5 h-3.5 text-primary-500/80" aria-hidden="true" />
                            <span className="text-mono-label text-neutral-300">{position.type}</span>
                          </div>
                          <div className="flex items-center gap-2 surface-inset rounded-pill px-3.5 py-2">
                            <DollarSign className="w-3.5 h-3.5 text-primary-500/80" aria-hidden="true" />
                            <span className="text-mono-label text-neutral-300">{position.salary}</span>
                          </div>
                        </div>
                      </div>
                      <Link
                        to="/contact"
                        className="btn-accent sheen flex-shrink-0"
                      >
                        Apply Now
                        <span className="sr-only"> for {position.title}</span>
                        <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
                      </Link>
                    </div>

                    <p className="text-body text-neutral-300 mb-8 max-w-3xl">{position.description}</p>

                    <div className="hairline mb-8" aria-hidden="true"></div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                      <div>
                        <h4 className="text-mono-label text-primary-500 mb-5">Requirements</h4>
                        <ul className="space-y-2.5">
                          {position.requirements?.map((req, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <div className="w-1 h-1 bg-primary-500 rounded-full mt-2.5 flex-shrink-0" aria-hidden="true"></div>
                              <span className="text-body-small text-neutral-300">{req}</span>
                            </li>
                          ))}
                        </ul>

                        {position.responsibilities && position.responsibilities.length > 0 && (
                          <div className="mt-8">
                            <h4 className="text-mono-label text-primary-500 mb-5">Key Responsibilities</h4>
                            <ul className="space-y-2.5">
                              {position.responsibilities.map((resp, idx) => (
                                <li key={idx} className="flex items-start gap-3">
                                  <div className="w-1 h-1 bg-primary-500 rounded-full mt-2.5 flex-shrink-0" aria-hidden="true"></div>
                                  <span className="text-body-small text-neutral-300">{resp}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      <div>
                        <h4 className="text-mono-label text-primary-500 mb-5">Benefits</h4>
                        <ul className="space-y-2.5">
                          {position.benefits?.map((benefit, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <div className="w-1 h-1 bg-primary-500 rounded-full mt-2.5 flex-shrink-0" aria-hidden="true"></div>
                              <span className="text-body-small text-neutral-300">{benefit}</span>
                            </li>
                          ))}
                        </ul>

                        {position.applications !== undefined && (
                          <div className="mt-8 surface-inset rounded-xl p-5">
                            <div className="flex items-center gap-2.5 text-sm text-neutral-400">
                              <Users className="w-4 h-4 text-primary-500/80" aria-hidden="true" />
                              <span>{position.applications} applications received</span>
                            </div>
                            <div className="flex items-center gap-2.5 text-sm text-neutral-400 mt-2">
                              <Clock className="w-4 h-4 text-primary-500/80" aria-hidden="true" />
                              <span>Posted {new Date(position.postedDate).toLocaleDateString()}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </article>
              </Reveal>
            )) : (
              <Reveal>
                <div className="surface-raised edge-lit rounded-card-lg text-center py-16 px-8 max-w-2xl mx-auto">
                  <div className="w-16 h-16 surface-raised-gold rounded-2xl flex items-center justify-center mx-auto mb-7">
                    <Briefcase className="w-7 h-7 text-primary-400" aria-hidden="true" />
                  </div>
                  <h3 className="text-heading-1 text-white mb-4">Nothing open this month</h3>
                  <p className="text-body-small text-neutral-400 mb-8 max-w-md mx-auto">
                    Roles here open when a mandate does, which is rarely on a schedule. Send us your
                    work anyway — we read what comes in.
                  </p>
                  <Link to="/contact" className="btn-accent sheen">
                    Get in Touch
                    <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
                  </Link>
                </div>
              </Reveal>
            )}
          </div>
        </div>
      </section>

      {/* Benefits & Culture */}
      <section id="culture" className="section-padding bg-black scroll-mt-20 relative overflow-hidden grain">
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>

        <div className="max-w-page mx-auto px-6 sm:px-8 lg:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-16 lg:mb-20">
            <Reveal className="max-w-3xl lg:order-2">
              <p className="text-mono-label text-primary-500 mb-5 flex items-center gap-3">
                <span>02 / 03</span>
                <span className="w-8 h-px bg-primary-500/40" aria-hidden="true"></span>
                <span>What the job comes with</span>
              </p>
              <h2 className="text-section-title text-white mb-6 text-balance">
                The offer, in plain terms
              </h2>
              <p className="text-body-large text-neutral-300">
                We build pay programmes for a living, so ours had better stand up to the same
                questions. Here is what sits behind the salary line.
              </p>
              <p className="text-body-small text-neutral-400 mt-6">
                Base pay, benefits, variable pay and equity are one structure, not four separate
                conversations. That is how we build a client&apos;s stack, and how we built ours.
              </p>
            </Reveal>

            <Reveal delay={120} className="lg:order-1">
              <div className="surface-inset rounded-card-lg border border-white/5 p-4 sm:p-6">
                <RewardsArchitecture className="w-full h-auto max-w-[460px] mx-auto" />
              </div>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {benefits.map((benefit, index) => (
              <Reveal key={index} className="scene h-full" delay={index * 80}>
                {/* No `.sheen` here — its overflow:hidden would flatten
                    preserve-3d and cancel the .tilt-raise lift. */}
                <TiltCard className="surface-raised edge-lit rounded-card h-full">
                  <div className="depth-layer p-8 h-full flex flex-col">
                    <div className="w-14 h-14 surface-raised-gold rounded-xl flex items-center justify-center mb-7 tilt-raise">
                      <benefit.icon className="w-6 h-6 text-primary-400" aria-hidden="true" />
                    </div>

                    <h3 className="text-heading-1 text-white mb-4 text-balance">{benefit.title}</h3>
                    <p className="text-body-small text-neutral-400">{benefit.description}</p>
                  </div>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-black relative overflow-hidden grain ambient-gold">
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>

        <div className="max-w-page mx-auto px-6 sm:px-8 lg:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.55fr)] gap-10 lg:gap-16 items-center mb-16 lg:mb-20">
            <Reveal className="max-w-3xl">
              <p className="text-mono-label text-primary-500 mb-5 flex items-center gap-3">
                <span>03 / 03</span>
                <span className="w-8 h-px bg-primary-500/40" aria-hidden="true"></span>
                <span>How we work</span>
              </p>
              <h2 className="text-section-title text-white mb-6 text-balance">
                Four rules we hold each other to
              </h2>
              <p className="text-body-large text-neutral-300">
                Not a poster in a corridor. These are the things that get raised in a review when
                somebody lets them slide.
              </p>
            </Reveal>

            <Reveal delay={120} className="w-full max-w-[300px] mx-auto lg:mx-0 lg:ml-auto">
              <BrandPrimitives
                variant="column"
                className="w-full aspect-[360/340]"
                label="Three brand primitives stacked in a column, standing for the order the work runs in: scope, build, present."
              />
              <p className="text-body-small text-neutral-400 mt-6 text-center lg:text-left">
                The order is the point. Scope it, defend the maths, own it through the first cycle
                that runs on it.
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {values.map((value, index) => (
              <Reveal key={index} delay={index * 100}>
                <div className="surface-raised edge-lit rounded-card p-8 lg:p-10 h-full">
                  <span
                    className="block text-mono-label text-primary-500/70 mb-5"
                    aria-hidden="true"
                  >
                    {String(index + 1).padStart(2, '0')} / {String(values.length).padStart(2, '0')}
                  </span>
                  <h3 className="text-heading-1 text-white mb-4">{value.title}</h3>
                  <p className="text-body-small text-neutral-400">{value.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-black relative overflow-hidden grain">
        <div className="absolute top-0 left-0 w-full hairline" aria-hidden="true"></div>

        <div className="max-w-page mx-auto container-padding relative">
          <Reveal>
            <div className="surface-raised-gold edge-lit rounded-arc px-8 py-16 lg:px-16 lg:py-20 text-center">
              <BrandPrimitives
                variant="row"
                className="w-full max-w-[260px] aspect-[360/340] mx-auto mb-8"
                label="Three brand primitives set in a row, the cube, cone and sphere of the Total Rewards Pro mark."
              />

              <h2 className="text-display-lg text-white mb-8 text-balance">
                No role that fits? Write anyway
              </h2>
              <p className="text-body-large text-neutral-300 mb-10 max-w-2xl mx-auto text-balance">
                Tell us about a pay problem you have solved and how you knew it worked. That reads
                better here than a list of tools.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact" className="btn-accent sheen px-8 py-3.5">
                  Get in Touch
                  <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
                </Link>
                <a
                  href="mailto:info@totalrewardspro.com"
                  className="inline-flex items-center justify-center rounded-md px-8 py-3.5 text-sm font-semibold text-white surface-raised sheen border border-white/10 transition-colors duration-300 hover:border-primary-500/60 hover:text-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-black font-open-sans"
                >
                  Send Your Resume
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  )
}

export default CareersPage
